import React, { useRef, useState } from 'react';
import { Button, ActionSheet, Toast } from 'antd-mobile';
import { Action } from 'antd-mobile/es/components/action-sheet';
import photoService, { PhotoSource } from '../services/PhotoService';
import { isNativeBridgeAvailable, ImageCompressionOptions } from './PhotoNativeBridge';
import { isIOS } from '../../../utils/deviceUtils';
import { pickWebImageFile, WEB_SELECTION_CANCELLED_CODE } from '../../../utils/storageUtils';
import { t } from 'i18n';
import H5PreviewShutterModal from './H5PreviewShutterModal';

/**
 * 是否像「带 native 桥的 iOS WKWebView」：看 window.webkit.messageHandlers。
 * 与 deviceUtils.isIOS（UA 判 iPhone/iPad）不同，仅用于 ActionSheet 默认行为。
 */
const hasIosWkMessageHandlers = (): boolean => {
  return typeof window !== 'undefined' && window.webkit?.messageHandlers !== undefined;
};

const WEB_IMAGE_SIZE_LIMIT_BYTES = 2 * 1024 * 1024;
const H5_STREAM_UNAVAILABLE_CODE = 'H5_STREAM_UNAVAILABLE';

/** onPhotoSelected 内已 Toast 的上传类错误，避免与下方「拍照失败」重复弹层 */
const isLikelyDownstreamUploadError = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message : String(error);
  return /File upload failed|Failed to upload file|File size exceeds 2MB limit/i.test(msg);
};

const isSelectionCancelledError = (error: unknown): boolean => {
  // 1) 优先用 code 判断（避免语言切换导致 message 文案不一致）
  if (typeof error === 'object' && error != null) {
    const maybeCode = (error as any).code;
    if (maybeCode === WEB_SELECTION_CANCELLED_CODE) {
      return true;
    }
  }

  // 2) 兼容旧逻辑：仍按 message 做兜底匹配
  if (error instanceof Error) {
    return error.message === t('pages.delivery.detail.photo.selectionCancelled');
  }

  return false;
};

interface PhotoSelectorProps {
  /** 允许返回 Promise（如注册上传）；PhotoSelector 会 await 并接住拒绝避免未捕获异常 */
  onPhotoSelected: (file: File, source: PhotoSource) => unknown;
  buttonText?: string | React.ReactNode;
  buttonProps?: any;
  children?: React.ReactNode;
  showActionSheet?: boolean;
  disabled?: boolean;
  compressionOptions?: ImageCompressionOptions; // 图片压缩选项
  webOnly?: boolean;
  onBeforeCapture?: () => Promise<boolean>; // 拍照前回调（如请求相机权限），返回 false 则中断拍照
}

const PhotoSelector: React.FC<PhotoSelectorProps> = ({
  onPhotoSelected,
  buttonText,
  buttonProps,
  showActionSheet,
  disabled = false,
  compressionOptions,
  webOnly = false,
  onBeforeCapture,
}) => {
  // 根据平台设置 showActionSheet 的默认值
  // webOnly + iOS：不展示自定义 ActionSheet，点击按钮直接调起系统选图（拍照/图片/文件），避免多一层 H5 菜单。
  // webOnly + 非 iOS：仍默认展示 ActionSheet（如 Android 上区分 getUserMedia 与相册）。
  // 非 webOnly：带 iOS WK 桥时默认直出相册，否则展示 ActionSheet。
  const defaultShowActionSheet =
    showActionSheet ??
    (webOnly && isIOS() ? false : webOnly ? true : (hasIosWkMessageHandlers() ? false : true));
  const [loading, setLoading] = useState(false);
  const [actionSheetVisible, setActionSheetVisible] = useState(false);
  const [h5PreviewVisible, setH5PreviewVisible] = useState(false);
  const h5CaptureRef = useRef<{
    resolve: (f: File) => void;
    reject: (e: Error) => void;
  } | null>(null);

  const postProcessWebImage = async (originalFile: File): Promise<File> => {
    let file = originalFile;
    const targetCompressionOptions = compressionOptions ?? {
      maxWidth: 1080,
      maxHeight: 1080,
      quality: 75,
      enableResize: true,
    };
    const shouldCompress =
      originalFile.size > WEB_IMAGE_SIZE_LIMIT_BYTES ||
      compressionOptions !== undefined;

    if (shouldCompress) {
      file = await photoService.compressImage(file, {
        maxWidth: targetCompressionOptions.maxWidth ?? 1080,
        maxHeight: targetCompressionOptions.maxHeight ?? 1080,
        quality: targetCompressionOptions.quality ?? 75,
        enableResize: targetCompressionOptions.enableResize ?? true,
      });
    }
    return file;
  };

  const pickWebImage = (capture?: 'environment') =>
    pickWebImageFile(capture, postProcessWebImage);

  const requestH5PreviewShot = (): Promise<File> => {
    return new Promise((resolve, reject) => {
      h5CaptureRef.current = { resolve, reject };
      setH5PreviewVisible(true);
    });
  };

  const handleH5Captured = (rawFile: File) => {
    const ref = h5CaptureRef.current;
    h5CaptureRef.current = null;
    setH5PreviewVisible(false);
    ref?.resolve(rawFile);
  };

  const handleH5PreviewClose = () => {
    const ref = h5CaptureRef.current;
    h5CaptureRef.current = null;
    setH5PreviewVisible(false);
    if (!ref) {
      return;
    }
    const err = new Error(t('pages.delivery.detail.photo.selectionCancelled'));
    (err as any).code = WEB_SELECTION_CANCELLED_CODE;
    ref.reject(err);
  };

  const handleH5StreamError = () => {
    const ref = h5CaptureRef.current;
    h5CaptureRef.current = null;
    setH5PreviewVisible(false);
    if (!ref) {
      return;
    }
    const err = new Error('H5 camera stream unavailable');
    (err as any).code = H5_STREAM_UNAVAILABLE_CODE;
    ref.reject(err);
  };

  const handleTakePhoto = async (useFrontCamera: boolean = false) => {
    if (onBeforeCapture) {
      const shouldContinue = await onBeforeCapture();
      if (!shouldContinue) return;
    }
    setLoading(true);
    try {
      let file: File;
      if (webOnly) {
        try {
          const raw = await requestH5PreviewShot();
          file = await postProcessWebImage(raw);
        } catch (e: unknown) {
          const code = typeof e === 'object' && e != null ? (e as any).code : undefined;
          if (code === H5_STREAM_UNAVAILABLE_CODE) {
            // H5 预览流不可用时：用 input[capture=environment] 尽量直开后置相机（含 Android）。
            file = await pickWebImage('environment');
          } else {
            throw e;
          }
        }
      } else {
        file = await photoService.takePhoto(useFrontCamera, compressionOptions);
      }
      await Promise.resolve(onPhotoSelected(file, 'camera'));
    } catch (error) {
      if (isSelectionCancelledError(error)) {
        return;
      }
      console.error('Photo capture failed:', error);
      if (isLikelyDownstreamUploadError(error)) {
        return;
      }
      const errorMessage = error instanceof Error ? error.message : String(error);
      Toast.show({ 
        content: t('pages.delivery.detail.photo.cameraCaptureFailed', { errorMessage }), 
        position: 'bottom' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFromGallery = async () => {
    setLoading(true);
    try {
      const file = webOnly
        ? await pickWebImage()
        : await photoService.selectFromGallery(compressionOptions);
      await Promise.resolve(onPhotoSelected(file, 'gallery'));
    } catch (error) {
      if (isSelectionCancelledError(error)) {
        return;
      }
      console.error('Photo selection failed:', error);
      if (isLikelyDownstreamUploadError(error)) {
        return;
      }
      const errorMessage = error instanceof Error ? error.message : String(error);
      Toast.show({ 
        content: t('pages.delivery.detail.photo.selectionFailed', { errorMessage }), 
        position: 'bottom' 
      });
    } finally {
      setLoading(false);
    }
  };

  const showPhotoOptions = () => {
    if (defaultShowActionSheet) {
      setActionSheetVisible(true);
    } else {
      // 如果不显示ActionSheet，默认从相册选择
      handleSelectFromGallery();
    }
  };

  // webOnly 或存在原生桥时展示「拍照」；「从相册选择」与「取消」始终展示。
  const getActions = (): Action[] => {
    const actions: Action[] = [];

    // webOnly=true：先 getUserMedia；流失败时回退 input[capture=environment] 直开后置相机。
    if (webOnly || isNativeBridgeAvailable()) {
      actions.push(
        {
          text: t('pages.delivery.detail.completeTakePhoto'),
          key: 'camera',
          onClick: () => {
            setActionSheetVisible(false);
            handleTakePhoto(false);
          },
        },
      );
    }

    actions.push(
      {
        text: t('pages.delivery.detail.completeTakeSelect'),
        key: 'gallery',
        onClick: () => {
          setActionSheetVisible(false);
          handleSelectFromGallery();
        },
      },
      {
        text: t('pages.delivery.detail.cancel'),
        key: 'cancel',
        onClick: () => {
          setActionSheetVisible(false);
        },
      }
    );

    return actions;
  };

  return (
    <>
      {webOnly && (
        <H5PreviewShutterModal
          visible={h5PreviewVisible}
          onUserCancel={handleH5PreviewClose}
          onCaptured={handleH5Captured}
          onStreamError={handleH5StreamError}
        />
      )}
      <Button
        onClick={showPhotoOptions}
        loading={loading}
        block
        fill="none"
        disabled={disabled}
        {...buttonProps}
      >
        {buttonText}
      </Button>
      {defaultShowActionSheet && (
        <ActionSheet
          visible={actionSheetVisible}
          actions={getActions()}
          onClose={() => setActionSheetVisible(false)}
        />
      )}
    </>
  );
};

export default PhotoSelector;
